import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, Percent, Gauge, Trophy } from 'lucide-react';
import api from '../../services/api';
import { FinanceKpiStrip } from '../../components/crm/billing/SalesFinance';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import PageHeader from '../../components/PageHeader';

const EMPTY_METRICS = {
  conversionRate: 0, wonCount: 0, lostCount: 0, closedTotal: 0,
  avgDealValue: 0, avgSalesCycleDays: 0, openCount: 0, pipelineVelocity: 0, winRateByRep: [],
};

export default function SalesDashboardPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [loading, setLoading] = useState(true);
  const currency = useCompanyCurrency();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/deals/sales-metrics', { params: { from: from || undefined, to: to || undefined } })
      .then(res => setMetrics(res.data.metrics || EMPTY_METRICS))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const maxWon = Math.max(1, ...metrics.winRateByRep.map(r => r.won));

  return (
    <div className="w-full px-0 sm:px-2 pb-20 space-y-6">
      <PageHeader title="Sales Dashboard" subtitle="Conversion rate, win rate by rep, and pipeline velocity — computed from closed deals." />

      <FinanceKpiStrip />

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-[#5C5C5C]">Closed from</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-[#5C5C5C]">to</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
        </div>
        <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F3F3F3] disabled:opacity-50 ml-auto">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><Percent size={12} /> Conversion rate</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{metrics.conversionRate.toFixed(1)}%</p>
          <p className="text-xs text-[#706E6B] mt-1">{metrics.wonCount} won / {metrics.closedTotal} closed</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><TrendingUp size={12} /> Avg. deal value</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{formatCurrency(metrics.avgDealValue, currency)}</p>
          <p className="text-xs text-[#706E6B] mt-1">Across won deals</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><Gauge size={12} /> Avg. sales cycle</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{metrics.avgSalesCycleDays.toFixed(1)} days</p>
          <p className="text-xs text-[#706E6B] mt-1">Created → closed won</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><TrendingUp size={12} /> Pipeline velocity</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{formatCurrency(metrics.pipelineVelocity, currency)}<span className="text-xs font-medium text-[#706E6B]">/day</span></p>
          <p className="text-xs text-[#706E6B] mt-1">{metrics.openCount} open deals</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <h2 className="text-sm font-bold text-[#2E2E2E] mb-4 flex items-center gap-1.5"><Trophy size={14} /> Win Rate by Rep</h2>
        {metrics.winRateByRep.length === 0 && !loading && (
          <p className="text-sm text-[#706E6B] py-8 text-center">No closed deals in this range yet.</p>
        )}
        <div className="space-y-3">
          {metrics.winRateByRep.map(rep => (
            <div key={rep.assignedTo} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-[#2E2E2E]">{rep.assignedTo}</span>
                <span className="text-[#5C5C5C] tabular-nums">
                  {rep.won}W / {rep.lost}L &middot; {rep.winRate.toFixed(0)}% win rate &middot; {formatCurrency(rep.wonValue, currency)}
                </span>
              </div>
              <div className="w-full h-2 bg-[#F3F3F3] rounded-full overflow-hidden">
                <div className="h-full bg-[#0176D3] rounded-full" style={{ width: `${(rep.won / maxWon) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
